import Icons from "../../icons/sidebar";

export interface IMenuItem {
  title: string;
  Icon?: any;
  path?: string;
  /** Nested items. A item with children renders as a collapsible section. */
  subMenu?: IMenuItem[];
  permissions?: string[];
  users?: string[];
  visible: boolean;
  /** Kept for compatibility — a section header is simply an item with a subMenu. */
  isHeader?: boolean;
}

export const MenuList = (
  userType: string,
  isSystemAdmin: boolean,
  isEdifyStaff: boolean,
  isParishCoordinator: boolean,
  isSchoolProprietor: boolean
): IMenuItem[] => {
  const isStaff = isSystemAdmin || isEdifyStaff;

  return [
    {
      title: "Dashboard",
      Icon: Icons.HomeIcon,
      path: "/dashboard",
      visible: true,
    },
    {
      title: "Profile",
      Icon: Icons.PersonIcon,
      path: "/profile",
      visible: true,
    },

    // ------------------------------------------------------------ Parishes
    {
      title: "Parishes",
      Icon: Icons.ChurchIcon,
      visible: true,
      isHeader: true,
      subMenu: [
        {
          title: "All Parishes",
          Icon: Icons.ChurchIcon,
          path: "/parishes",
          visible: true,
        },
        {
          title: "Parish Statistics",
          Icon: Icons.AssessmentIcon,
          path: "/parishes/statistics",
          visible: true,
        },
        {
          title: "Coordinators",
          Icon: Icons.PersonIcon,
          path: "/admin/coordinators",
          visible: true,
        },
      ],
    },

    // ------------------------------------------------------------- Schools
    {
      title: "Schools",
      Icon: Icons.SchoolIcon,
      visible: true,
      isHeader: true,
      subMenu: [
        {
          title: "All Schools",
          Icon: Icons.SchoolIcon,
          path: "/schools",
          visible: true,
        },
        {
          title: "My School",
          Icon: Icons.HomeWorkIcon,
          path: "/schools/my-school",
          visible: true,
        },
        {
          title: "School Performance",
          Icon: Icons.TrendingUpIcon,
          path: "/schools/performance",
          visible: true,
        },
      ],
    },

    // ------------------------------------------------------------ Meetings
    {
      title: "Meetings",
      Icon: Icons.EventAvailableIcon,
      visible: true,
      isHeader: true,
      subMenu: [
        {
          title: "Upcoming Meetings",
          Icon: Icons.EventAvailableIcon,
          path: "/meetings",
          visible: true,
        },
        {
          title: "Meeting History",
          Icon: Icons.HistoryIcon,
          path: "/meetings",
          visible: true,
        },
        {
          title: "Schedule Meeting",
          Icon: Icons.EventAvailableIcon,
          path: "/meetings/new",
          visible: isParishCoordinator || isStaff,
        },
      ],
    },

    // --------------------------------------------------------- Assessments
    {
      title: "Assessments",
      Icon: Icons.AssignmentIcon,
      visible: true,
      isHeader: true,
      subMenu: [
        {
          title: "My Assessments",
          Icon: Icons.AssignmentIcon,
          path: "/assessments",
          visible: true,
        },
        {
          title: "New Assessment",
          Icon: Icons.AssignmentIcon,
          path: "/assessments/new",
          visible: isSchoolProprietor || isStaff || isParishCoordinator,
        },
        {
          title: "All Assessments",
          Icon: Icons.LibraryBooksIcon,
          path: "/assessments/all",
          visible: isStaff || isParishCoordinator,
        },
        {
          title: "Performance Indicators",
          Icon: Icons.ChecklistIcon,
          path: "/assessments/indicators",
          visible: true,
        },
        {
          title: "Manage Outcome Areas",
          Icon: Icons.EditIcon,
          path: "/assessments/outcome-areas/manage",
          visible: true,
        },
        {
          title: "Manage Indicators",
          Icon: Icons.EditIcon,
          path: "/assessments/indicators/manage",
          visible: true,
        },
        {
          title: "Champion Schools",
          Icon: Icons.ChecklistIcon,
          path: "/assessments/champions",
          visible: true,
        },
      ],
    },

    // ------------------------------------------------------------ Training
    {
      title: "Training",
      Icon: Icons.MenuBookIcon,
      visible: true,
      isHeader: true,
      subMenu: [
        {
          title: "Training Modules",
          Icon: Icons.MenuBookIcon,
          path: "/training",
          visible: true,
        },
        {
          title: "Sessions",
          Icon: Icons.EventAvailableIcon,
          path: "/training/sessions",
          visible: true,
        },
        {
          title: "My Progress",
          Icon: Icons.TrendingUpIcon,
          path: "/training/my-progress",
          visible: true,
        },
        {
          title: "Resources",
          Icon: Icons.FolderSharedIcon,
          path: "/training/resources",
          visible: true,
        },
        {
          title: "Manage Content",
          Icon: Icons.EditIcon,
          path: "/training/manage",
          visible: isStaff || isParishCoordinator,
        },
      ],
    },

    // ----------------------------------------------------------------- PLC
    {
      title: "PLC",
      Icon: Icons.GroupsIcon,
      visible: true,
      isHeader: true,
      subMenu: [
        {
          title: "My Communities",
          Icon: Icons.GroupsIcon,
          path: "/plc",
          visible: true,
        },
        {
          title: "Browse Communities",
          Icon: Icons.ExploreIcon,
          path: "/plc/browse",
          visible: true,
        },
        {
          title: "Create Community",
          Icon: Icons.GroupAddIcon,
          path: "/plc/browse",
          visible: isParishCoordinator || isSchoolProprietor || isStaff,
        },
      ],
    },

    // ------------------------------------------------------------- Reports
    {
      title: "Reports",
      Icon: Icons.BarChartIcon,
      visible: true,
      isHeader: true,
      subMenu: [
        {
          title: "Dashboard Analytics",
          Icon: Icons.BarChartIcon,
          path: "/reports",
          visible: true,
        },
        {
          title: "Parish Reports",
          Icon: Icons.DescriptionIcon,
          path: "/reports/parishes",
          visible: true,
        },
        {
          title: "School Reports",
          Icon: Icons.DescriptionIcon,
          path: "/reports/schools",
          visible: true,
        },
        {
          title: "Export Data",
          Icon: Icons.GetAppIcon,
          path: "/reports/export",
          visible: true,
        },
      ],
    },

    // ---------------------------------------------------------- Financials
    {
      title: "Financials",
      Icon: Icons.AccountBalanceIcon,
      visible: isStaff,
      isHeader: true,
      subMenu: [
        {
          title: "Subscriptions",
          Icon: Icons.SubscriptionsIcon,
          path: "/admin/financials/subscriptions",
          visible: true,
        },
        {
          title: "Fee Payments",
          Icon: Icons.ReceiptIcon,
          path: "/admin/financials/payments",
          visible: true,
        },
        {
          title: "Financial Reports",
          Icon: Icons.AssessmentIcon,
          path: "/admin/financials/reports",
          visible: true,
        },
      ],
    },

    // ------------------------------------------------------- Administration
    {
      title: "Administration",
      Icon: Icons.SettingsIcon,
      visible: true,
      isHeader: true,
      subMenu: [
        {
          title: "User Management",
          Icon: Icons.PeopleIcon,
          path: "/admin/users",
          visible: true,
        },
        {
          title: "Roles & Permissions",
          Icon: Icons.SecurityIcon,
          path: "/admin/roles",
          visible: true,
        },
        {
          title: "System Settings",
          Icon: Icons.SettingsIcon,
          path: "/admin/settings",
          visible: true,
        },
        {
          title: "Audit Logs",
          Icon: Icons.HistoryIcon,
          path: "/admin/audit-logs",
          visible: true,
        },
      ],
    },

    // ---------------------------------------------------------------- Help
    // Kept last so documentation sits below the working sections, the way
    // help is conventionally placed. Open to every signed-in role.
    {
      title: "Help",
      Icon: Icons.HelpOutlineIcon,
      visible: true,
      isHeader: true,
      subMenu: [
        {
          title: "User Guide",
          Icon: Icons.LibraryBooksOutlinedIcon,
          path: "/guide",
          visible: true,
        },
        {
          title: "Testing Guide",
          Icon: Icons.FactCheckIcon,
          path: "/test",
          visible: true,
        },
      ],
    },
  ];
};

export default MenuList;
